// frontend/src/pages/partner/WalletPartner.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import './WalletPartner.css';

const WalletPartner = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [services, setServices] = useState([]);
  const [approvisionnements, setApprovisionnements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApprovModal, setShowApprovModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [pointsToApprovisionner, setPointsToApprovisionner] = useState('');
  const [msg, setMsg] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, servicesRes, approvRes] = await Promise.all([
        API.get('/wallet/my-wallet'),
        API.get('/companies/my'),
        API.get('/wallet/approvisionnements')
      ]);

      setWallet(walletRes.data.wallet);
      setServices(servicesRes.data.companies || []);
      setApprovisionnements(approvRes.data.approvisionnements || []);
    } catch (err) {
      console.error('Erreur chargement données:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovisionner = async () => {
    if (!selectedService || !pointsToApprovisionner || pointsToApprovisionner <= 0) {
      setMsg({ type: 'error', text: 'Veuillez sélectionner un service et saisir un nombre de points valide' });
      return;
    }

    if (pointsToApprovisionner > wallet.available_points) {
      setMsg({ type: 'error', text: `Points insuffisants. Maximum: ${wallet.available_points}` });
      return;
    }

    setLoadingAction(true);
    try {
      const res = await API.post('/wallet/approvisionner', {
        company_id: selectedService.id,
        points: parseInt(pointsToApprovisionner)
      });

      setMsg({ type: 'success', text: res.data.message });
      setShowApprovModal(false);
      setSelectedService(null);
      setPointsToApprovisionner('');
      await fetchData();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || "Erreur lors de l'approvisionnement" });
    } finally {
      setLoadingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Chargement de votre portefeuille...</p>
      </div>
    );
  }

  const usagePercentage = wallet?.total_points > 0
    ? ((wallet.points_approvisionnes / wallet.total_points) * 100).toFixed(0)
    : 0;

  return (
    <div className="wallet-partner-pro">
      {/* ── Hero ── */}
      <motion.div
        className="wp-hero"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        <div className="wp-hero-orb wp-hero-orb-1" />
        <div className="wp-hero-orb wp-hero-orb-2" />
        <div className="wp-hero-left">
          <div className="wp-hero-icon"><i className="fas fa-wallet" /></div>
          <div>
            <h1>Portefeuille de points</h1>
            <p>Gérez vos points et approvisionnez vos services</p>
          </div>
        </div>
        <div style={{ zIndex: 1 }}>
          <button
            className="btn-allocate-pro"
            onClick={() => setShowApprovModal(true)}
            disabled={wallet?.available_points === 0}
          >
            <i className="fas fa-plus-circle"></i>
            Approvisionner un service
          </button>
        </div>
      </motion.div>

      {/* Messages */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`alert-pro ${msg.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cartes KPIs */}
      <div className="kpi-grid-pro">
        <div className="kpi-card-pro total">
          <div className="kpi-icon">
            <i className="fas fa-coins"></i>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Points totaux</span>
            <span className="kpi-value">{wallet?.total_points?.toLocaleString()}</span>
          </div>
          <div className="kpi-trend">
            <i className="fas fa-chart-line"></i>
            <span>+{((wallet?.total_points / 1000) || 0).toFixed(0)}% ce mois</span>
          </div>
        </div>

        <div className="kpi-card-pro allocated">
          <div className="kpi-icon">
            <i className="fas fa-chart-pie"></i>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Points approvisionnés</span>
            <span className="kpi-value">{wallet?.points_approvisionnes?.toLocaleString()}</span>
          </div>
          <div className="kpi-progress">
            <div className="progress-bar" style={{ width: `${usagePercentage}%` }}></div>
          </div>
        </div>

        <div className="kpi-card-pro available">
          <div className="kpi-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Points disponibles</span>
            <span className="kpi-value">{wallet?.available_points?.toLocaleString()}</span>
          </div>
          <button
            className="btn-buy-points"
            onClick={() => navigate('/dashboard/partner/purchase-packs')}
          >
            <i className="fas fa-cart-plus"></i> Acheter
          </button>
        </div>
      </div>

      {/* Section Approvisionnements */}
      <div className="allocations-section-pro">
        <div className="section-header-pro">
          <h3>
            <i className="fas fa-layer-group"></i>
            Approvisionnements par service
          </h3>
          <span className="allocations-count">{approvisionnements.length} services</span>
        </div>

        {approvisionnements.length === 0 ? (
          <div className="empty-state-pro">
            <i className="fas fa-box-open"></i>
            <h4>Aucun approvisionnement</h4>
            <p>Commencez par approvisionner vos services en points</p>
            <button
              className="btn-primary"
              onClick={() => setShowApprovModal(true)}
            >
              <i className="fas fa-plus"></i>
              Approvisionner un service
            </button>
          </div>
        ) : (
          <div className="allocations-grid-pro">
            {approvisionnements.map((approv, index) => {
              const service = services.find(s => s.id === approv.company_id);
              const usageRate = approv.points_approvisionnes > 0
                ? ((approv.used_points / approv.points_approvisionnes) * 100).toFixed(0)
                : 0;

              return (
                <div
                  key={approv.company_id}
                  className="allocation-card-pro"
                >
                  <div className="card-header">
                    <div className="service-icon">
                      <i className="fas fa-cube"></i>
                    </div>
                    <div className="service-info">
                      <h4>{service?.name || 'Service'}</h4>
                      <span className="usage-badge">{usageRate}% utilisé</span>
                    </div>
                  </div>

                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Approvisionnés</span>
                      <strong className="stat-value allocated">{approv.points_approvisionnes.toLocaleString()}</strong>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Utilisés</span>
                      <strong className="stat-value used">{approv.used_points.toLocaleString()}</strong>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Restants</span>
                      <strong className="stat-value remaining">{approv.remaining_points.toLocaleString()}</strong>
                    </div>
                  </div>

                  <div className="progress-container">
                    <div className="progress-label">
                      <span>Progression</span>
                      <span>{usageRate}%</span>
                    </div>
                    <div className="progress-bar-bg">
                      <div
                        className="progress-fill"
                        style={{ width: `${usageRate}%` }}
                      />
                    </div>
                  </div>

                  <button
                    className="btn-view-packs"
                    onClick={() => navigate(`/dashboard/partner/b2c-packs?service=${approv.company_id}`)}
                  >
                    <i className="fas fa-boxes"></i>
                    Gérer les packs
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL D'APPROVISIONNEMENT */}
      <AnimatePresence>
        {showApprovModal && (
          <div
            className="modal-overlay-pro"
            onClick={() => setShowApprovModal(false)}
          >
            <div
              className="modal-container-pro"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header-pro">
                <h2>
                  <i className="fas fa-arrow-right"></i>
                  Approvisionner un service
                </h2>
                <button className="modal-close" onClick={() => setShowApprovModal(false)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body-pro">
                <div className="wallet-info-pro">
                  <i className="fas fa-wallet"></i>
                  <div>
                    <span>Points disponibles</span>
                    <strong>{wallet?.available_points?.toLocaleString()} pts</strong>
                  </div>
                </div>

                <div className="form-group-pro">
                  <label>Service</label>
                  <select
                    value={selectedService?.id || ''}
                    onChange={(e) => {
                      const service = services.find(s => s.id === parseInt(e.target.value));
                      setSelectedService(service);
                    }}
                  >
                    <option value="">Choisir un service</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group-pro">
                  <label>Nombre de points</label>
                  <div className="input-group-pro">
                    <input
                      type="number"
                      min="1"
                      max={wallet?.available_points}
                      value={pointsToApprovisionner}
                      onChange={(e) => setPointsToApprovisionner(e.target.value)}
                      placeholder="0"
                    />
                    <span className="input-suffix">pts</span>
                  </div>
                  <small>Minimum: 1 point | Maximum: {wallet?.available_points?.toLocaleString()} points</small>
                </div>
              </div>

              <div className="modal-footer-pro">
                <button
                  className="btn-cancel-pro"
                  onClick={() => setShowApprovModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn-confirm-pro"
                  onClick={handleApprovisionner}
                  disabled={loadingAction || !selectedService || !pointsToApprovisionner}
                >
                  {loadingAction ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Approvisionnement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      Approvisionner {parseInt(pointsToApprovisionner)?.toLocaleString() || 0} points
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WalletPartner;
